/**
 * TanStack Query hook for `GET /api/cro/me`.
 *
 * Returns the authenticated Entra principal's UPN + CRO group membership.
 * Used by EntraPrincipalChip (ENTRA-02) in the AppShell footer.
 *
 * Config:
 *   - queryKey: ['cro', 'me']
 *   - staleTime: 5 min — identity is session-stable; the chip doesn't need
 *     fresh data on every navigation.
 *   - retry: 1 — if the /api/cro/me endpoint fails it's likely a transient
 *     server error; one retry is sufficient before showing "Identity unavailable".
 */
import { useQuery, type UseQueryResult } from '@tanstack/react-query';

import {
  apiFetch,
  type ApiClientError,
  type ApiServerError,
  type UnauthenticatedError,
} from '../lib/api-client.js';

import type { CroMeResponse } from '@saledash/shared';

type MetricError = ApiServerError | ApiClientError | UnauthenticatedError;

export function useCroMe(): UseQueryResult<CroMeResponse, MetricError> {
  // noLoginRedirect: this hook is used in BOTH places that strictly require
  // CRO access (Finance Upload, Admin Targets) AND places that just gate UI
  // chrome (EntraPrincipalChip, cross-link). For a general SF user on the
  // apex /main, /api/cro/me will 401 because they haven't signed in via
  // Easy Auth — and they may not be in the CRO group at all, so bouncing
  // them to Entra sign-in is wrong. The 401 surfaces as data===undefined
  // + isError; the gating UI treats that as "no CRO access" and hides
  // itself. Pages that need the user to actually sign in handle the
  // redirect themselves (see FinanceUploadPage requireCroAccess).
  return useQuery<CroMeResponse, MetricError>({
    queryKey: ['cro', 'me'],
    queryFn: ({ signal }) =>
      apiFetch<CroMeResponse>('/api/cro/me', { signal, noLoginRedirect: true }),
    staleTime: 5 * 60 * 1000, // 5 min — identity is stable for the session
    retry: 1,
  });
}
